import { gql } from "lib/apolloClient";

export const userNameFragment = gql`
  fragment userNameFields on User {
    id
    firstName
    middleName
    lastName
    type
    ... on Student {
      preferredName
      pronouns
      suffix
      prefix
    }
    ... on Staff {
      preferredName
      pronouns
      suffix
      prefix
    }
    ... on FamilyMember {
      preferredName
      pronouns
      suffix
      prefix
    }
  }
`;

export const studentsAlertFragment = gql`
  fragment alertTypeFields on StudentAlertType {
    id
    name
    icon
  }
`;

export const similarityFragment = {
  similarityItem: gql`
    fragment similarityItem on SimilarityReport {
      status
      submissionId
      errorCode
      overallMatchPercentage
    }
  `,
};

export const taggedPracticesFragment = {
  taggedPractices: gql`
    fragment taggedPracticesOnEvidence on SnPTaggedPractice {
      evaluationCycle {
        id
      }
      taggedPractice {
        taggedBy {
          id
        }
        practice {
          id
        }
        evidenceStatus
      }
    }
  `,
};

export const excusalFragment = {
  basicExcusalDetails: gql`
    fragment excusalItem on AttendanceExcusal {
      id
      durationType
      startDate
      endDate
      reason
      periods {
        id
        label
        abbreviation
      }
    }
  `,
};

export const studentAttendanceV2Fragment = {
  basicAttendanceV2Details: gql`
    fragment studentAttendanceV2Item on StudentDateAttendance {
      id
      date
      remark
      period {
        id
        label
      }
      course {
        id
        title
      }
      isHomeroomAttendance
      value {
        id
        label
        abbreviation
        color
        status
        isDefault
        sendSMS
        sendEmail
        attendanceCategory {
          id
          color
        }
      }
    }
  `,
};

export const courseFragment = {
  basicCourserDetails: gql`
    fragment courseItem on Course {
      id
      title
      isArchived
    }
  `,
  courserGradesDetails: gql`
    fragment courseGradeItem on Course {
      id
      title
      profileImageData {
        url
        icon
      }
      profileImage
      isArchived
      grades {
        id
        name
      }
      subjects {
        id
      }
    }
  `,
  courseGradeSubjectDetails: gql`
    fragment courseGradeSubjectItem on Course {
      id
      title
      profileImage
      profileImageData {
        url
        icon
        acronym
      }
      isArchived
      grades {
        id
        name
      }
      subjects {
        id
      }
    }
  `,
};

export const nodeEditorFragment = {
  MultiLevelNodeBenchmarkDetails: gql`
    fragment MultiLevelNodeBenchmarkItem on MultiLevelNodeBenchmark {
      id
      label
      subText
      parentId
      children
      depth
      code
      grades {
        id
        name
      }
      tags {
        id
      }
      benchmarkQuestionCount {
        count
        url
        color
        yearSplit
      }
      # type # causing issue with the type in PlannerElementNode but this is not used in NodeEditor
      subjectId
    }
  `,
  MultiLevelNodePlannerElementDetails: gql`
    fragment MultiLevelNodePlannerElementItem on MultiLevelNodePlannerElement {
      id
      label
      subText
      parentId
      children
      depth
      code
      grades {
        id
        name
        __typename
      }
      tags {
        id
        __typename
      }
      __typename
    }
  `,
};

export const notificationConnectionFragment = {
  notificationConnection: gql`
    fragment notificationConnectionItem on NotificationConnection {
      totalCount
      newCount
      earlierCount
      categorisedNewCount {
        category
        count
      }
      categorisedTotalCount {
        category
        count
      }
    }
  `,
};

export const pageInfoFragment = {
  pageInfo: gql`
    fragment pageInfoItem on PageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
  `,
};

export const notificationFragment = {
  staffCountItem: gql`
    fragment staffCountItem on Staff {
      id
      notification(notificationCategoryFilter: $notificationCategoryFilter) {
        ...notificationConnectionItem
      }
    }
    ${notificationConnectionFragment.notificationConnection}
  `,
  studentCountItem: gql`
    fragment studentCountItem on Student {
      id
      notification(notificationCategoryFilter: $notificationCategoryFilter) {
        ...notificationConnectionItem
      }
    }
    ${notificationConnectionFragment.notificationConnection}
  `,
  parentCountItem: gql`
    fragment parentCountItem on FamilyMember {
      id
      notification(notificationCategoryFilter: $notificationCategoryFilter) {
        ...notificationConnectionItem
      }
    }
    ${notificationConnectionFragment.notificationConnection}
  `,
  notificationItem: gql`
    fragment notificationItem on Notification {
      id
      type
      isNew
      isRead
      metadata
      category
      createdBy {
        id
        firstName
        lastName
        profileImage
      }
      createdAt
      updatedBy {
        id
        firstName
        lastName
        profileImage
      }
      course {
        ...courseItem
        grades {
          id
          name
        }
        curriculumProgram {
          id
          type
        }
      }
      courses {
        id
        title
        curriculumProgram {
          id
          type
        }
      }
      updatedAt
      messageData
    }
    ${courseFragment.basicCourserDetails}
  `,
};

export const notificationListNodeFragment = {
  staff: gql`
    fragment staffItem on Staff {
      id
      notification(
        appType: WEB
        notificationCategoryFilter: $notificationCategoryFilter
      ) {
        ...notificationConnectionItem
        edge(first: 10, after: $after) {
          cursor
          node {
            ...notificationItem
          }
        }
        pageInfo(first: 10, after: $after) {
          ...pageInfoItem
        }
      }
    }
    ${pageInfoFragment.pageInfo}
    ${notificationConnectionFragment.notificationConnection}
    ${notificationFragment.notificationItem}
  `,
  student: gql`
    fragment studentItem on Student {
      id
      notification(
        appType: WEB
        notificationCategoryFilter: $notificationCategoryFilter
      ) {
        ...notificationConnectionItem
        edge(first: 10, after: $after) {
          cursor
          node {
            ...notificationItem
          }
        }
        pageInfo(first: 10, after: $after) {
          ...pageInfoItem
        }
      }
    }
    ${pageInfoFragment.pageInfo}
    ${notificationConnectionFragment.notificationConnection}
    ${notificationFragment.notificationItem}
  `,
  parent: gql`
    fragment parentItem on FamilyMember {
      id
      notification(
        appType: WEB
        notificationCategoryFilter: $notificationCategoryFilter
      ) {
        ...notificationConnectionItem
        edge(first: 10, after: $after) {
          cursor
          node {
            ...notificationItem
          }
        }
        pageInfo(first: 10, after: $after) {
          ...pageInfoItem
        }
      }
    }
    ${pageInfoFragment.pageInfo}
    ${notificationConnectionFragment.notificationConnection}
    ${notificationFragment.notificationItem}
  `,
};

export const designationFragment = {
  designation: gql`
    fragment designationItem on StaffDesignation {
      id
      title
      description
      roles
    }
  `,
};

export const parentFragment = {
  basicParentDetails: gql`
    fragment parentItem on FamilyMember {
      id
      firstName
      middleName
      lastName
      profileImage
      type
      Email: email
      isDevUser
    }
  `,
};

export const periodFragmentV2 = {
  period: gql`
    fragment periodItem on Period {
      id
      label
      type
      isDefault
      startTime
      endTime
      sequence
      isDefault
      abbreviation
    }
  `,
};

export const periodFragment = {
  period: gql`
    fragment periodItem on Period {
      id
      label
      type
      isDefault
      startTime
      endTime
      sequence
      isDefault
      abbreviation
    }
  `,
};

export const periodSetFragmentV2 = {
  periodSet: gql`
    fragment periodSetItem on PeriodSet {
      id
      count
      periods {
        ...periodItem
      }
    }
    ${periodFragmentV2.period}
  `,
};

export const periodSetFragment = {
  periodSet: gql`
    fragment periodSetItem on PeriodSet {
      id
      count
      periods {
        ...periodItem
      }
    }
    ${periodFragment.period}
  `,
};

export const curriculumFragment = {
  curriculum: gql`
    fragment curriculumItem on Curriculum {
      id
      label
      type
    }
  `,
};

export const curriculumProgramFragment = {
  curriculumProgramBasicDetails: gql`
    fragment curriculumProgramBasicDetailsItem on CurriculumProgram {
      id
      type
      nodeInterfaceType
      label
      acronym
      academicSetupType
      programType
      ... on IbDpCurriculumProgram {
        examSessionMonth
      }
    }
  `,
  subscriptionPlanDetails: gql`
    fragment subscriptionPlanDetailsItem on CurriculumProgram {
      id
      type
      subscriptionPlan {
        id
        type
        isFree
        label
      }
    }
  `,
  periodSet: gql`
    fragment curriculumProgramItem on CurriculumProgram {
      id
      type
      nodeInterfaceType
      label
      acronym
      curriculum {
        ...curriculumItem
      }
      periodSet(filter: $periodSetFilter) {
        ...periodSetItem
      }
      attendanceRecordingType
    }
    ${curriculumFragment.curriculum}
    ${periodSetFragment.periodSet}
  `,
  periodSetV2: gql`
    fragment curriculumProgramItem on CurriculumProgram {
      id
      type
      nodeInterfaceType
      label
      acronym
      curriculum {
        ...curriculumItem
      }
      periodSet(filter: $periodSetFilter) {
        ...periodSetItem
      }
      attendanceRecordingType
    }
    ${curriculumFragment.curriculum}
    ${periodSetFragmentV2.periodSet}
  `,
};

export const childFragment = {
  basicChildInfo: gql`
    fragment childItem on Student {
      id
      firstName
      middleName
      lastName
      preferredName
      prefix
      suffix
      pronouns
      type
    }
  `,
  basicChildDetails: gql`
    fragment childItem on Student {
      id
      email
      type
      jwt
      profileImage
      inviteExpireTime
      firstName
      middleName
      lastName
      preferredName
      pronouns
      prefix
      suffix
      isDevUser
      parentStatus(parentId: $id)
      isRequestAccepted
      sourceId
      familyInviteCode
      signInCode
      isBlocked
      blockMessage
    }
  `,

  childDetails: gql`
    fragment childDetailsItem on Student {
      curriculumProgram {
        id
        type
      }
      curriculumPrograms {
        id
        type
      }
      allCourses {
        id
        title
        academicYears {
          id
        }
        curriculumProgram {
          ...curriculumProgramBasicDetailsItem
        }
      }
      organization {
        id
        name
        tier {
          isFree
        }
        academicYears {
          id
          isCurrentAcademicYear
        }
      }
    }
    ${curriculumProgramFragment.curriculumProgramBasicDetails}
  `,
};

export const coachMarkFragment = {
  coachMark: gql`
    fragment coachMarkItem on CoachMarkUserStatus {
      id
      coachMark
      isViewed
      count
    }
  `,
};

export const staffFragment = {
  basicStaffDetailsCommunity: gql`
    fragment basicStaffDetailsItem on Staff {
      id
      firstName
      middleName
      lastName
      type
      profileImage
      email
      showPitchScreen
      showClassSelectionScreen
      showBifurcationScreen
      schoolTenures(currentSchool: true) {
        id
        school {
          id
          name
        }
      }
    }
  `,
  basicStaffDetails: gql`
    fragment staffItem on Staff {
      id
      firstName
      middleName
      lastName
      preferredName
      prefix
      suffix
      pronouns
      profileImage
      email
      type
      showPitchScreen
      role
      showClassSelectionScreen
      showBifurcationScreen
      isDevUser
      isArchived
      entityTags {
        id
        key
        value
      }
    }
  `,
  staffCoachMarks: gql`
    fragment staffCoachMarksItem on Staff {
      id
      coachMarkStatus {
        ...coachMarkItem
      }
    }
    ${coachMarkFragment.coachMark}
  `,
  userIdentityBasicDetail: gql`
    fragment userIdentityBasicDetailItem on Staff {
      id
      firstName
      middleName
      lastName
      profileImage
      type
    }
  `,
};

export const selectedPeriodFragment = {
  selectedPeriods: gql`
    fragment selectedPeriodItem on SelectedPeriod {
      id
      isSelected
      day
      rotationDay
      location
      course {
        ...courseItem
      }
      period {
        ...periodItem
        abbreviation
      }
      teachers {
        ...staffItem
      }
      subject
      startTime
      endTime
    }
    ${courseFragment.basicCourserDetails}
    ${periodFragment.period}
    ${staffFragment.basicStaffDetails}
  `,
  selectedPeriodsV2: gql`
    fragment selectedPeriodItemV2 on TimetableSlot {
      date
      isSelected
      day
      rotationDay {
        id
        label
      }
      location
      course {
        ...courseItem
      }
      period {
        ...periodItem
        abbreviation
      }
      teachers {
        ...staffItem
      }
      subject
      startTime
      endTime
    }
    ${courseFragment.basicCourserDetails}
    ${periodFragment.period}
    ${staffFragment.basicStaffDetails}
  `,
  selectedPeriodsInfo: gql`
    fragment selectedPeriodInfoItem on SelectedPeriod {
      id
      isSelected
      period {
        id
      }
    }
  `,
};

export const staffPeriodListFragment = {
  staffPeriodList: gql`
    fragment staffPeriodListItem on Staff {
      id
      firstName
      lastName
      enrolledPeriods(filters: $periodFilters) {
        ...selectedPeriodItem
      }
    }
    ${selectedPeriodFragment.selectedPeriods}
  `,
  staffPeriodListV2: gql`
    fragment staffPeriodListItemV2 on Staff {
      id
      firstName
      lastName
      enrolledPeriodsV2(filters: $periodFilters) {
        ...selectedPeriodItemV2
      }
    }
    ${selectedPeriodFragment.selectedPeriodsV2}
  `,
};

export const studentFragment = {
  /*
   this fragment is being used in getCourseStudentListWithInviteCodesQuery which gets triggered in family invite class link flow,
   this query is called with parent's jwt with orgId undefined. Before making any changes to this fragment, please keep this case in consideration
  */
  basicStudentDetails: gql`
    fragment studentItem on Student {
      id
      firstName
      middleName
      lastName
      pronouns
      preferredName
      suffix
      prefix
      profileImage
      email
      type
      isDevUser
      isArchived
      sourceId
      type
    }
  `,
  studentCourseMap: gql`
    fragment studentItem on Student {
      id
      courseMap(filters: { courseIds: $courseIds }) {
        id
        tags {
          id
          label
        }
      }
    }
  `,
  basicStudentDetailsWithExcusal: gql`
    fragment studentDetailsWithExcusalItem on Student {
      id
      firstName
      middleName
      lastName
      preferredName
      suffix
      pronouns
      profileImage
      email
      type
      isDevUser
      isArchived
      isArchivedV2(filters: { academicYearId: $academicYearId })
      enrollmentDate
      sourceId
      studentAttendanceExcusals(filters: $filters) {
        totalCount
        edges {
          node {
            ...excusalItem
          }
        }
      }
      courseMap(filters: { courseIds: $courseIds }) {
        id
        tags {
          id
          label
        }
      }
      attendanceV2(filters: $attendanceV2Filters) {
        edges {
          node {
            ...studentAttendanceV2Item
          }
        }
      }
    }
    ${excusalFragment.basicExcusalDetails}
    ${studentAttendanceV2Fragment.basicAttendanceV2Details}
  `,
  basicStudentDetailsWithFamilyMembers: gql`
    fragment studentWithFamilyMembersItem on Student {
      id
      firstName
      middleName
      lastName
      profileImage
      email
      type
      preferredName
      suffix
      pronouns
      isDevUser
      isArchived
      isArchivedV2(filters: { academicYearId: $academicYearId })
      enrollmentDate
      sourceId
      familyMembers {
        id
        firstName
        lastName
        middleName
        profileImage
        email
      }
      courseMap(filters: { courseIds: $courseIds }) {
        id
        tags {
          id
          label
        }
      }
      studentAttendanceExcusals(filters: $attendanceExcusalFilters) {
        totalCount
        edges {
          node {
            id
            reason
          }
        }
      }
      attendanceV2(filters: $attendanceV2Filters) {
        edges {
          node {
            ...studentAttendanceV2Item
          }
        }
      }
    }
    ${studentAttendanceV2Fragment.basicAttendanceV2Details}
  `,
  basicStudentDetailsWithBasicCourse: gql`
    fragment studentWithBasicCourseItem on Student {
      id
      firstName
      middleName
      lastName
      profileImage
      email
      preferredName
      suffix
      pronouns
      type
      isDevUser
      isArchived
      sourceId
      courses(filters: $courseFilters) {
        id
        title
      }
    }
  `,
  studentCoachMarks: gql`
    fragment studentCoachMarksItem on Student {
      id
      coachMarkStatus {
        ...coachMarkItem
      }
    }
    ${coachMarkFragment.coachMark}
  `,
  studentPeriodList: gql`
    fragment studentPeriodListItem on Student {
      id
      firstName
      middleName
      lastName
      enrolledPeriods(filters: $periodFilters) {
        ...selectedPeriodItem
      }
    }
    ${selectedPeriodFragment.selectedPeriods}
  `,
  studentPeriodListV2: gql`
    fragment studentPeriodListItemV2 on Student {
      id
      firstName
      lastName
      enrolledPeriodsV2(filters: $periodFilters) {
        ...selectedPeriodItemV2
      }
    }
    ${selectedPeriodFragment.selectedPeriodsV2}
  `,
  studentPeriodListInfo: gql`
    fragment studentPeriodListInfoItem on Student {
      id
      enrolledPeriods(filters: $periodFilters) {
        ...selectedPeriodInfoItem
      }
    }
    ${selectedPeriodFragment.selectedPeriodsInfo}
  `,
};

export const schoolInfoFragment = {
  userSchoolInfo: gql`
    fragment userSchoolInfo on Staff {
      schoolTenures(currentSchool: true) {
        id
        school {
          id
          name
        }
      }
    }
  `,
};

export const userDetailsHashFragment = gql`
  fragment userDetailsHashItem on UserDetailsHash {
    appId
    sprinklrChatHash
    userServiceId
    organizationId
  }
`;

export const userFragment = {
  basicUser: gql`
    fragment userItem on User {
      id
      firstName
      middleName
      lastName
      type
      email
      profileImage
      ... on Student {
        preferredName
        prefix
        suffix
        pronouns
        type
        sourceId
      }
      ... on Staff {
        preferredName
        prefix
        suffix
        pronouns
        role
        courses {
          id
          title
        }
        curriculumPrograms {
          id
          acronym
        }
        isArchived
      }
    }
  `,
  basicUserDetails: gql`
    fragment userItem on User {
      id
      firstName
      middleName
      lastName
      profileImage
      email
      type
      ... on FamilyMember {
        prefix
        suffix
        preferredName
        pronouns
      }
      ... on Staff {
        preferredName
        prefix
        suffix
        pronouns
        role
        ...userSchoolInfo
      }
      ... on Student {
        preferredName
        prefix
        suffix
        pronouns
      }
    }
    ${schoolInfoFragment.userSchoolInfo}
  `,
  /**
   * @deprecated
   */
  linkedIdentityDetails: gql`
    fragment linkedIdentityDetailsItem on User {
      id
      type
      ... on Staff {
        designation {
          id
          title
        }
        organization {
          id
          name
          logo
        }
        jwt
      }
      ... on Student {
        organization {
          id
          name
          logo
        }
        jwt
      }
    }
  `,
};

export const identityUserFragment = {
  basicDetails: gql`
    fragment identityUserBasicDetailsItem on IdentityUser {
      id
      identityId
      firstName
      lastName
      type
      organization {
        id
        name
        logo
      }
      jwt
      designation {
        id
        title
      }
      isAccessBlockedDueToMfa
      isMfaEnabled
      mfaEnabledMethods {
        name
        metadata
      }
    }
  `,
};

export const academicYearFragment = {
  academicYear: gql`
    fragment academicYearItem on AcademicYear {
      id
      startDate
      endDate
      isCurrentAcademicYear
      isUpcomingAcademicYear
      transitionReviewStatus
      label
    }
  `,
  academicYearBasicDetails: gql`
    fragment academicYearBasicDetailsItem on AcademicYear {
      id
      startDate
      endDate
      isCurrentAcademicYear
      isUpcomingAcademicYear
      isPreviousAcademicYear
      dependentEntities {
        id
      }
    }
  `,
  termBasicDetails: gql`
    fragment termBasicDetailsItem on AcademicTerm {
      id
      label
      startDate
      endDate
      curriculumProgram {
        id
      }
      dependentEntities {
        id
      }
    }
  `,
  academicYearChildAcademicYearDetails: gql`
    fragment academicYearChildAcademicYearDetailsItem on AcademicYear {
      id
      childAcademicYears {
        academicYear {
          id
          startDate
          endDate
          isCurrentAcademicYear
          isUpcomingAcademicYear
        }
        curriculumProgram {
          id
        }
      }
    }
  `,
};

export const unitTypeFragment = {
  unitType: gql`
    fragment unitTypeItem on UnitType {
      id
      feedLabel
      name
      curriculum {
        ...curriculumItem
      }
    }
    ${curriculumFragment.curriculum}
  `,
};

export const plannerTemplateFragment = {
  plannerTemplate: gql`
    fragment templateItem on PlannerTemplate {
      id
      body
      name
      type
      plannerName
      plannerFormat
      unitType
      isBilingual
      curriculum {
        ...curriculumItem
      }
      helpEnabledFields {
        id
        uid
        helpText {
          id
        }
        type
      }
      subType
    }
    ${curriculumFragment.curriculum}
  `,
  localisedPlannerTemplate: gql`
    fragment localisedTemplateItem on PlannerTemplate {
      id
      body(locale: $locale)
      name
      type
      plannerName
      plannerFormat
      unitType
      isBilingual
      isGlobal
      curriculum {
        ...curriculumItem
      }
      helpEnabledFields {
        id
        uid
        helpText {
          id
        }
        type
      }
      subType
    }
    ${curriculumFragment.curriculum}
  `,
  plannerTemplateBasicDetails: gql`
    fragment templateBasicDetailsItem on PlannerTemplate {
      id
      name
      type
      plannerName
      assessmentType
      plannerFormat
      unitType
      curriculumProgram {
        id
      }
      curriculum {
        ...curriculumItem
      }
    }
    ${curriculumFragment.curriculum}
  `,
};

export const plannerTemplateHelpTextFragment = {
  plannerTemplateHelpText: gql`
    fragment helpTextItem on HelpText {
      id
      title
      subtitle
      content
    }
  `,
};

export const focusAreaFragment = {
  focusArea: gql`
    fragment focusAreaItem on FocusArea {
      id
      label
    }
  `,
  focusAreaSet: gql`
    fragment focusAreaSetItem on PlannerFocusAreaSet {
      id
      label
    }
  `,
};

export const pypElementFragment = {
  theme: gql`
    fragment themeItem on PlannerTheme {
      id
      label
      descriptor
      metadata
      associatedChildren {
        id
        label
      }
      focusAreas {
        ...focusAreaItem
      }
      icon
    }
    ${focusAreaFragment.focusArea}
  `,
  lp: gql`
    fragment lpItem on PlannerLearnerProfile {
      id
      label
      subText
      levelId
      parent
      children
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  `,
  concept: gql`
    fragment conceptItem on PlannerConcept {
      id
      label
      subText
      levelId
      parent
      children
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  `,
  atl: gql`
    fragment atlItem on PlannerATL {
      id
      label
      children
      parent
      levelId
      levels {
        id
        value
      }
      depth
      isLeaf
      displaySequence
      atlCategory {
        id
      }
      subText
      grades {
        id
        name
      }
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  `,
  benchmark: gql`
    fragment benchmarkItem on PlannerBenchmark {
      id
      label
      subText
      children
      parent
      benchmarkQuestionCount {
        count
        color
        url
        yearSplit
      }
      levels {
        id
        value
        count
      }
      subject
      levelId
      displaySequence
      depth
      type
      code
      grades {
        id
        name
      }
      isLeaf
      genericTags: tags {
        id
        label
        icon {
          id
          url
        }
      }
      resolvedSubject {
        id
        isArchived
      }
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  `,
  benchmarkV2: gql`
    fragment benchmarkItem on PlannerBenchmark {
      id
      label
      subText
      children
      parentId: parent
      benchmarkQuestionCount {
        count
        color
        url
        yearSplit
      }
      subject
      depth
      code
      grades {
        id
        name
      }
      genericTags: tags {
        id
        label
      }
      resolvedSubject {
        id
        isArchived
      }
    }
  `,
  benchmarkStandard: gql`
    fragment benchmarkStandardItem on StandardBenchmark {
      id
      label
      children
      parent
      parentId
      subject
      levelId
      displaySequence
      depth
      type
      code
      grades {
        id
        name
      }
      isLeaf
    }
  `,
  relatedConcept: gql`
    fragment relatedConceptItem on PlannerRelatedConcept {
      id
      label
      elementId
      elementType
      parent
      children
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  `,
  action: gql`
    fragment actionItem on Action {
      id
      label
      subText
      children
      parent
      levels {
        id
        value
      }
      levelId
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  `,
  plannerElementNode: gql`
    fragment plannerElementNode on PlannerElementNode {
      id
      label
      depth
      children
      type
    }
  `,
};

export const standardSetFragment = {
  standardSet: gql`
    fragment standardSetItem on BenchmarkStandardSet {
      id
      title
      description
      code
    }
  `,
  standardSetDetails: gql`
    fragment standardSetItem on BenchmarkStandardSet {
      id
      title
      description
      framework
      snsChildren
      benchmarks {
        rootNodes
        levels {
          id
          value
        }
        maxDepth
        nodes {
          ...benchmarkStandardItem
        }
      }
    }
    ${pypElementFragment.benchmarkStandard}
  `,
  standardSetFeed: gql`
    fragment standardSetItem on BenchmarkStandardSet {
      id
      title
      description
      framework
      snsChildren
    }
  `,
};

export const atlsCategoryFragment = {
  basicAtlCategoryDetails: gql`
    fragment atlCategoryItem on ATLCategory {
      id
      label
      subText
      grades {
        id
      }
    }
  `,
};

export const plannerElementDepthFragment = {
  plannerElementDepthLabels: gql`
    fragment plannerElementDepthLabelItem on PlannerElementDepthLabel {
      id
      depth
      label
      totalNodes
    }
  `,
};

export const thirdPartyAccountFragment = {
  thirdPartyAccount: gql`
    fragment thirdPartyAccountItem on LinkedThirdPartyAccount {
      id
      serviceType
      accessToken @skip(if: $skipAccessToken)
      isConnected @include(if: $skipAccessToken)
    }
  `,
};

export const genericTagFragment = {
  genericTag: gql`
    fragment basicGenericTagItem on GenericTag {
      id
      type
      label
    }
  `,
  genericTagDetails: gql`
    fragment genericTagDetailsItem on GenericTag {
      id
      label
      displaySequence
      icon {
        id
        url
      }
    }
  `,
};

export const subjectFragment = {
  basicSubjectDetails: gql`
    fragment subjectItem on Subject {
      id
      name
      isArchived
    }
  `,
  subjectWithSubjectGroup: gql`
    fragment subjectWithSubjectGroupItem on Subject {
      id
      name
      type
      icon
      metadata
      grades {
        id
        name
      }
      subjectGroup {
        id
        name
        constants
        type
      }
      variants {
        level {
          ...basicGenericTagItem
        }
        responseLanguages {
          ...basicGenericTagItem
        }
      }
      flatVariants {
        isArchived
        level {
          ...basicGenericTagItem
        }
        responseLanguage {
          ...basicGenericTagItem
        }
      }
      ibOfferedSubject {
        id
        contentSubject {
          id
          assessmentYearTitle
        }
      }
      isArchived
    }
    ${genericTagFragment.genericTag}
  `,
  subjectDetails: gql`
    fragment subjectItem on Subject {
      id
      name
      icon
      type
      grades {
        id
        name
      }
      courses {
        edges {
          node {
            ...courseGradeItem
          }
        }
        totalCount
      }
      isArchived
    }
    ${courseFragment.courserGradesDetails}
  `,
  subjectDetailsV2: gql`
    fragment subjectItem on Subject {
      id
      name
      grades {
        id
        name
      }
      courses {
        totalCount
      }
      subjectGroup {
        id
        name
      }
      isInterdisciplinary
      metadata
      associatedChildPlannerElementNodes(filters: $plannerNodeFilters) {
        id
        label
        type
        isLeaf
        children
        subText
      }
      flatVariants(filters: $flatSubjectVariantFilters) {
        responseLanguage {
          id
          label
        }
        isArchived
      }
      isArchived
    }
  `,
  subjectBenchmarkDetails: gql`
    fragment subjectItem on Subject {
      id
      name
      icon
      grades {
        id
        name
      }
      isArchived
      benchmarkRootNode {
        ...benchmarkItem
      }
      isArchived
      globalSubject
      framework
      snsChildren
      standardSet {
        ...standardSetItem
      }
      subjectLevelLabels {
        cuLevelName
        strandLevelName
        loLevelName
      }
      type
      tags {
        id
        key
        value
      }
      variants {
        level {
          ...basicGenericTagItem
        }
        responseLanguages {
          ...basicGenericTagItem
        }
      }
      flatVariants(filters: { archivalState: ALL }) {
        isArchived
        level {
          ...basicGenericTagItem
        }
        responseLanguage {
          ...basicGenericTagItem
        }
      }
    }
    ${genericTagFragment.genericTag}
    ${pypElementFragment.benchmark}
    ${standardSetFragment.standardSet}
  `,
  subjectPlannerElementDepth: gql`
    fragment subjectPlannerElementDepthItem on Subject {
      id
      name
      icon
      type
      isSyncSubject
      grades {
        id
        name
      }
      isArchived
      plannerElementDepthLabelSets(filters: $plannerElementDepthLabelFilter) {
        plannerElementDepthLabels {
          ...plannerElementDepthLabelItem
        }
      }
      standardSet {
        id
        title
        code
      }
      subjectGroup {
        id
        name
        displaySequence
      }
      ibOfferedSubject {
        id
        contentSubject {
          id
          assessmentYearTitle
        }
      }
      tags {
        id
        key
        value
      }
    }
    ${plannerElementDepthFragment.plannerElementDepthLabels}
  `,
  subjectsBasicDetails: gql`
    fragment subjectsBasicDetails on Subject {
      id
      name
      icon
      grades {
        id
        name
      }
      unitPlans {
        totalCount
      }
      courses {
        totalCount
      }
      tags {
        id
        key
        value
      }
      isArchived
    }
  `,
};

export const gradeFragment = {
  basicGradeDetails: gql`
    fragment gradeItem on Grade {
      id
      name
      globalGrade {
        id
        uid
        name
        constants
      }
      curriculumProgram {
        ...curriculumProgramBasicDetailsItem
      }
      displaySequence
    }
    ${curriculumProgramFragment.curriculumProgramBasicDetails}
  `,
  gradeDetails: gql`
    fragment gradeItem on Grade {
      id
      name
      subjects(filters: { archivalState: ALL }) {
        ...subjectItem
        benchmarkRootNode {
          ...benchmarkItem
        }
        subjectGroup {
          id
          name
        }
      }
      atlCategories {
        ...atlCategoryItem
      }
    }
    ${pypElementFragment.benchmark}
    ${subjectFragment.subjectBenchmarkDetails}
    ${atlsCategoryFragment.basicAtlCategoryDetails}
  `,
  gradeUnitDetails: gql`
    fragment gradeItem on Grade {
      id
      name
      unitPlanCount(innerUserIdFilter: $id)
        @include(if: $isCurriculumProgramFree)
      globalGrade {
        id
        constants
        displaySequence
      }
      displaySequence
    }
  `,
  gradeWithSubjectBenchmarkRootNodesBasicDetails: gql`
    fragment gradeItem on Grade {
      id
      name
      subjects(filters: $gradeSubjectsFilter) {
        ...subjectItem
        benchmarkRootNode {
          ...benchmarkItem
        }
        subjectGroup {
          id
        }
      }
    }
    ${pypElementFragment.benchmark}
    ${subjectFragment.subjectBenchmarkDetails}
  `,
};

export const subjectGroupFragment = {
  subjectGroupBasicDetails: gql`
    fragment subjectGroupBasicDetailsItem on SubjectGroup {
      id
      name
      constants
    }
  `,
  subjectGroupWithGlobalSubjectGroup: gql`
    fragment subjectGroupWithGlobalSubjectGroupItem on SubjectGroup {
      id
      name
      constants
      globalSubjectGroup {
        id
        name
        constants
      }
    }
  `,
  subjectGroupWithSubjects: gql`
    fragment subjectGroupItem on SubjectGroup {
      id
      name
      globalSubjectGroup{
        id
        constants
      }
      subjects(filters: $subjectFilters) {
        ...subjectItem
      }
      curriculumProgram {
       ...curriculumProgramBasicDetailsItem
      }
      sourceSubjects {
        id
        name
      }
    }
    ${subjectFragment.subjectDetails}
    ${curriculumProgramFragment.curriculumProgramBasicDetails},
  `,
};

export const plannerElementNodeFragment = {
  basicPlannerElementNodeDetail: gql`
    fragment plannerElementNodeItem on PlannerElementNode {
      id
      label
      subText
    }
  `,
  plannerElementNode: gql`
    fragment plannerElementNodeItem on PlannerElementNode {
      id
      displaySequence
      label
      subText
      code
      type
      subText
      depth
      children
      parent
      subType
      isLeaf
      code
      metadata
      updatedAt
      associatedParents {
        id
        ... on Grade {
          id
          name
        }
        ... on Subject {
          id
          name
          isArchived
        }
        ... on SubjectGroup {
          ...subjectGroupBasicDetailsItem
        }
        ... on PlannerElementNode {
          id
          label
          type
          metadata
        }
      }
      tags: genericTags {
        id
        label
        icon {
          id
          url
        }
      }
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
    ${subjectGroupFragment.subjectGroupBasicDetails}
  `,
  plannerElementNodeWithChild: gql`
    fragment plannerElementNodeWithChildItem on PlannerElementNode {
      id
      label
      type
      associatedParents {
        id
        ... on Course {
          id
          title
        }
      }
      associatedChildPlannerElementNodes {
        id
        label
        type
        fields {
          id
          uid
          value
        }
      }
    }
  `,
  plannerElementDepthLabels: gql`
    fragment plannerElementDepthLabelItem on PlannerElementDepthLabel {
      id
      depth
      label
      totalNodes
    }
  `,
};

export const plannerElementNodeWithDepthLabelFragment = {
  plannerElementNode: gql`
    fragment plannerElementNodeItem on PlannerElementNode {
      id
      label
      type
      subText
      depth
      children
      parent
      isLeaf
      code
      plannerElementDepthLabelSets(
        filters: $plannerElementDepthLabelSetFilters
      ) {
        plannerElementDepthLabels {
          totalNodes
          label
          depth
          id
        }
      }
      associatedParents {
        id
        ... on Grade {
          id
          name
        }
        ... on Subject {
          id
          name
        }
        ... on SubjectGroup {
          id
          name
        }
        ... on PlannerElementNode {
          id
          label
          type
        }
      }
    }
  `,
};

export const plannerElementSetWithDepthLabelFragment = {
  plannerElementSetWithNodes: gql`
    fragment plannerElementSetWithNodesItem on PlannerElementSet {
      id
      label
      type
      depthLabels {
        id
        depth
        label
      }
      nodes(filters: $filters) {
        ...plannerElementNodeItem
      }
    }
    ${plannerElementNodeWithDepthLabelFragment.plannerElementNode}
  `,
};

export const plannerElementSetFragment = {
  plannerElementSetWithNodes: gql`
    fragment plannerElementSetWithNodesItem on PlannerElementSet {
      id
      label
      type
      nodes(filters: $filters) {
        ...plannerElementNodeItem
      }
    }
    ${plannerElementNodeFragment.plannerElementNode}
  `,
  plannerElementSetWithNodesAndChild: gql`
    fragment plannerElementSetWithNodesAndChildItem on PlannerElementSet {
      id
      label
      type
      nodes(filters: $filters) {
        ...plannerElementNodeWithChildItem
      }
    }
    ${plannerElementNodeFragment.plannerElementNodeWithChild}
  `,
  plannerElementSetBasicDetails: gql`
    fragment plannerElementSetBasicDetailsItem on PlannerElementSet {
      id
      label
      type
    }
  `,
  plannerElementAssociatedChildPlannerElementNodes: gql`
    fragment plannerElementAssociatedChildPlannerElementNodesItem on PlannerElementNode {
      id
      label
      type
    }
  `,
};

export const organizationGradesFragment = {
  organizationGrades: gql`
    fragment organizationGradesItem on Organization {
      id
      grades(filters: $filters) {
        ...gradeItem
      }
    }
    ${gradeFragment.basicGradeDetails}
  `,
};

export const organizationCoursesFragment = {
  organizationCourses: gql`
    fragment organizationCourseItem on Organization {
      id
      courses(orderBy: UPDATED_AT, orderByDirection: ASC, filters: $filters) {
        edges {
          node {
            ...courseGradeItem
            grades {
              id
              globalGrade {
                id
                constants
              }
            }
            collaborators: allCollaborators {
              totalCount
            }
          }
        }
      }
    }
    ${courseFragment.courserGradesDetails}
  `,
  organizationBasicCourses: gql`
    fragment organizationCourseItem on Organization {
      id
      courses(orderBy: TITLE, orderByDirection: ASC, filters: $filters) {
        edges {
          node {
            ...courseItem
          }
        }
      }
    }
    ${courseFragment.basicCourserDetails}
  `,
};

export const criteriaValueFragment = {
  criteriaValue: gql`
    fragment criteriaValueItem on AcademicCriteriaValue {
      id
      label
      abbreviation
      description
      color
      localGrades {
        id
        label
        localGradeCriteriaValue {
          id
          label
        }
      }
      scoreValues {
        id
        minValue
        maxValue
      }
    }
  `,
};

export const criteriaValueSlimFragment = {
  criteriaValueSlim: gql`
    fragment criteriaValueSlimItem on AcademicCriteriaValue {
      id
      label
      abbreviation
      description
      color
    }
  `,
};

export const criteriaSetSlimFragment = {
  criteriaSetSlim: gql`
    fragment criteriaSetSlimItem on AcademicCriteriaSet {
      id
      label
      criteriaType
      subType
      isArchived
      grades {
        id
      }
      curriculumProgram {
        id
      }
      academicCriteriaValues {
        ...criteriaValueSlimItem
      }
    }
    ${criteriaValueSlimFragment.criteriaValueSlim}
  `,
};

export const criteriaSetFragment = {
  criteriaSet: gql`
    fragment criteriaSetItem on AcademicCriteriaSet {
      id
      label
      academicCriteriaValues {
        ...criteriaValueItem
      }
      grades {
        ...gradeItem
      }
      criteriaType
      subType
      isArchived
      curriculumProgram {
        id
      }
    }
    ${gradeFragment.basicGradeDetails}
    ${criteriaValueFragment.criteriaValue}
  `,
};

export const attachmentFragment = {
  attachment: gql`
    fragment attachmentItem on Attachment {
      id
      name
      type
      mimeType
      url
      signedUrl
      thumbUrl
      title
      metadata
      streamUrl
      parentType
      isRead
      similarityReport {
        ...similarityItem
      }
      createdBy {
        id
        type
      }
      lockingState {
        state
        scheduledAt
      }
      questionAttachment {
        ... on QuestionSubmission {
          id
          questionScore
          score {
            id
            value
          }
        }
        ... on Question {
          id
          questionScore
        }
      }
    }
    ${similarityFragment.similarityItem}
  `,
  basicDetails: gql`
    fragment attachmentBasicDetailsItem on Attachment {
      id
      url
      signedUrl
      thumbUrl
      metadata
      name
      type
    }
  `,
};

export const pypElementRatingFragment = {
  organizationElementRating: gql`
    fragment ratingItem on OrganizationElementRatings {
      id
      ibPYPElementType
      levelId
      ibPYPParentElementId
      academicCriteriaSets {
        id
      }
      subType
    }
  `,
  studentElementRating: gql`
    fragment ratingItem on IBPYPElementRating {
      id
      value
      ibPYPElementId
      ibPYPElementType

      academicCriteriaSet {
        ...criteriaSetItem
      }
      academicCriteriaValue {
        ...criteriaValueItem
      }
    }

    ${criteriaSetFragment.criteriaSet}
    ${criteriaValueFragment.criteriaValue}
  `,
  studentElementRatingSlim: gql`
    fragment ratingItem on IBPYPElementRating {
      id
      ibPYPElementId
      ibPYPElementType
      academicCriteriaSet {
        id
      }
      academicCriteriaValue {
        id
      }
    }
  `,
  basicIBPYPElementRating: gql`
    fragment basicIBPYPElementRatingItem on IBPYPElementRating {
      id
      ibPYPElementId
      value
      academicCriteriaSet {
        id
        label
      }
      academicCriteriaValue {
        id
        label
        abbreviation
      }
    }
  `,
  elementRemark: gql`
    fragment remarkItem on IbPypElementRemark {
      id
      message
      ibPYPElementId
      ibPYPElementType
    }
  `,
};

export const toolResponseFragment = {
  toolResponse: gql`
    fragment toolResponseItem on AssessmentToolResponse {
      id
      rowId
      columnId
      subjectiveResponse
      creatorType
      isDeleted
      evaluationJustification {
        id
        message
      }
    }
  `,
};

/**
 * @deprecated
 */
export const organizationPypElementSetFragment = {
  conceptSet: gql`
    fragment organizationPypElementSetItem on Organization {
      id
      pypElement {
        concepts {
          rootNodes
          nodes {
            ...conceptItem
          }
        }
      }
    }
    ${pypElementFragment.concept}
  `,
  lpSet: gql`
    fragment organizationPypElementSetItem on Organization {
      id
      pypElement {
        learnerProfiles {
          rootNodes
          nodes {
            ...lpItem
          }
        }
      }
    }
    ${pypElementFragment.lp}
  `,
  atlSet: gql`
    fragment organizationPypElementSetItem on Organization {
      id
      pypElement {
        atls(filters: { grades: $grades }) {
          rootNodes
          nodes {
            ...atlItem
          }
        }
      }
    }
    ${pypElementFragment.atl}
  `,
  relatedConceptSet: gql`
    fragment organizationPypElementSetItem on Organization {
      id
      pypElement {
        relatedConcept(
          filters: { subjectIds: $subjects, conceptIds: $concepts }
        ) {
          rootNodes
          nodes {
            ...relatedConceptItem
          }
        }
      }
    }
    ${pypElementFragment.relatedConcept}
  `,
  benchmarkSet: gql`
    fragment organizationPypElementSetItem on Organization {
      id
      pypElement {
        benchmarks(
          filters: {
            subjects: $subjects
            grades: $grades
            unitPlanIds: $unitPlanIds
            genericTagIds: $genericTagIds
          }
        ) {
          rootNodes
          nodes {
            ...benchmarkItem
          }
        }
      }
    }
    ${pypElementFragment.benchmark}
  `,

  actionSet: gql`
    fragment organizationPypElementSetItem on Organization {
      id
      pypElement {
        actions {
          rootNodes
          nodes {
            ...actionItem
          }
        }
      }
    }
    ${pypElementFragment.action}
  `,
  themeSet: gql`
    fragment organizationPypElementSetItem on Organization {
      id
      pypElement {
        themes {
          ...themeItem
        }
      }
    }
    ${pypElementFragment.theme}
  `,
};

export const pypPlannerElementSetFragment = {
  lpSet: gql`
    fragment lpSetItem on PlannerLearnerProfileSet {
      id
      rootNodes
      nodes {
        ...lpItem
      }
    }
    ${pypElementFragment.lp}
  `,
  conceptSet: gql`
    fragment conceptSetItem on PlannerConceptSet {
      id
      rootNodes
      nodes {
        ...conceptItem
      }
    }
    ${pypElementFragment.concept}
  `,
  atlSet: gql`
    fragment atlSetItem on ATLSet {
      id
      rootNodes
      nodes(filters: { grades: $grades }) {
        ...atlItem
      }
    }
    ${pypElementFragment.atl}
  `,
  relatedConceptSet: gql`
    fragment relatedConceptSetItem on PlannerRelatedConceptSet {
      id
      rootNodes
      nodes(filters: { subjectIds: $subjects, conceptIds: $concepts }) {
        ...relatedConceptItem
      }
    }
    ${pypElementFragment.relatedConcept}
  `,
  themeSet: gql`
    fragment themeSetItem on PlannerThemeSet {
      id
      nodes(filters: { academicYear: $academicYear }) {
        ...themeItem
      }
    }
    ${pypElementFragment.theme}
  `,
  actionSet: gql`
    fragment actionSetItem on ActionSet {
      id
      rootNodes
      nodes {
        id
        label
        subText
        children
        parent
        levels {
          id
          value
        }
        levelId
      }
    }
  `,
  benchmarkSet: gql`
    fragment PlannerBenchmarkSetItem on PlannerBenchmarkSet {
      id
      label
      rootNodes
      nodes(
        filters: {
          grades: $grades
          subjects: $subjects
          unitPlanIds: $unitPlanIds
          genericTagIds: $genericTagIds
        }
      ) {
        ...benchmarkItem
      }
      __typename
    }
    ${pypElementFragment.benchmark}
  `,
  plannerElementSet: gql`
    fragment plannerElementSetWithNodesItem on PlannerElementSet {
      id
      label
      nodes(filters: $filters) {
        ...plannerElementNodeItem
      }
    }
    ${plannerElementNodeFragment.plannerElementNode}
  `,
};

export const genericPlannerElementSetFragment = {
  basicDetails: gql`
    fragment genericPlannerElementSetBasicDetailsItem on GenericPlannerElementSet {
      id
      label
      type
    }
  `,
  plannerElementSetWithNodes: gql`
    fragment plannerElementSetWithNodesItem on GenericPlannerElementSet {
      id
      type
      label
      ... on PlannerThemeSet {
        ...themeSetItem
      }
      ... on ATLSet {
        ...atlSetItem
      }
      ... on PlannerLearnerProfileSet {
        ...lpSetItem
      }
      ... on PlannerConceptSet {
        ...conceptSetItem
      }
      ... on ActionSet {
        ...actionSetItem
      }
      ... on PlannerFocusAreaSet {
        ...focusAreaSetItem
      }
    }
    ${pypPlannerElementSetFragment.themeSet}
    ${pypPlannerElementSetFragment.atlSet}
    ${pypPlannerElementSetFragment.lpSet}
    ${pypPlannerElementSetFragment.conceptSet}
    ${pypPlannerElementSetFragment.actionSet}
    ${focusAreaFragment.focusAreaSet}
  `,
};

export const organizationFragment = {
  organizationBasicDetails: gql`
    fragment organizationItem on Organization {
      id
      name
      logo
    }
  `,
  curriculumPrograms: gql`
    fragment curriculumProgramsItem on Organization {
      id
      curriculumPrograms {
        id
        label
        acronym
        programDataLanguages
        isPrimary
        academicSetupType
        terms {
          totalCount
        }
        ...subscriptionPlanDetailsItem
        ... on IbMypCurriculumProgram {
          id
          nodeInterfaceType
          type
          plannerElementSets {
            ...plannerElementSetBasicDetailsItem
          }
        }
        ... on IbPypCurriculumProgram {
          id
          nodeInterfaceType
          type
          plannerElementSets {
            ...genericPlannerElementSetBasicDetailsItem
          }
        }
        ... on UBDCurriculumProgram {
          id
          variant
          nodeInterfaceType
          type
          presetSubjectMap
          plannerElementSets {
            ...plannerElementSetBasicDetailsItem
          }
        }
        ... on IbDpCurriculumProgram {
          id
          nodeInterfaceType
          type
          examSessionMonth
          plannerElementSets {
            ...plannerElementSetBasicDetailsItem
          }
        }
      }
    }
    ${curriculumProgramFragment.subscriptionPlanDetails}
    ${plannerElementSetFragment.plannerElementSetBasicDetails}
    ${genericPlannerElementSetFragment.basicDetails}
  `,
  organizationConstants: gql`
    fragment organizationItem on Organization {
      id
      constants {
        calendarEventTags
        newsEventTags
        rubricLevels
        isEvaluatePypElement
        singlePointRubricCriterias
        projectGroupDefaultGrades
        ratingLevels {
          ...ratingItem
        }
        academicCriteriaSets {
          ...criteriaSetSlimItem
        }
        assessmentTemplate
        leTemplate
        settings
        enabledFeaturesList
        scheduler
        examSessionMonth
      }
    }
    ${criteriaSetSlimFragment.criteriaSetSlim}
    ${pypElementRatingFragment.organizationElementRating}
  `,
  organizationDetails: gql`
    fragment organizationItem on Organization {
      id
      name
      isPaid
      isLearningCourseEnabled
      ibSchoolCode
      personalProject {
        id
      }
      communityProject {
        id
      }
      serviceAsAction {
        id
      }
      grades {
        ...gradeItem
      }
      unitTypes {
        ...unitTypeItem
      }
      academicYears {
        ...academicYearItem
        childAcademicYears {
          academicYear {
            ...academicYearItem
          }
          curriculumProgram {
            id
            type
          }
        }
      }
      templates {
        ...templateBasicDetailsItem
      }
      tags {
        id
        key
        value
      }
      subjects {
        id
        name
      }
      repoOrganization {
        commonCourseId
      }
      oauthServiceName
      academicYearGroups {
        id
        name
        acronym
        isCurrent
        displayOrder
        academicYears {
          id
        }
        curriculumProgramWiseAcademicYearMap {
          curriculumProgram {
            id
          }
          academicYear {
            id
          }
        }
      }
    }
    ${plannerTemplateFragment.plannerTemplateBasicDetails}
    ${academicYearFragment.academicYear}
    ${gradeFragment.basicGradeDetails}
    ${unitTypeFragment.unitType}
  `,
  organizationSubjects: gql`
    fragment organizationItem on Organization {
      id
      subjects(filters: $filters) {
        ...subjectItem
      }
    }
    ${subjectFragment.subjectDetails}
  `,
  organizationSubjectsV2: gql`
    fragment organizationItem on Organization {
      id
      subjects(filters: $filters) {
        ...subjectItem
      }
    }
    ${subjectFragment.subjectDetailsV2}
  `,
  organizationSubjectGroups: gql`
    fragment organizationItem on Organization {
      id
      subjectGroups(filters: $filters) {
        ...subjectGroupItem
      }
    }
    ${subjectGroupFragment.subjectGroupWithSubjects}
  `,
  organizationGlobalConstants: gql`
    fragment organizationGlobalConstantItem on OrganizationGlobalConstants {
      id
      name
      value
      curriculumProgram {
        id
        type
      }
    }
  `,
  organizationSubjectsBasicDetails: gql`
    fragment organizationItem on Organization {
      id
      subjects(filters: $filters) {
        ...subjectsBasicDetails
      }
    }
    ${subjectFragment.subjectsBasicDetails}
  `,
  curriculumProgramAcademicYearDetails: gql`
    fragment curriculumProgramAcademicYearDetailsItem on CurriculumProgram {
      id
      academicYears {
        id
        startDate
        endDate
        isCurrentAcademicYear
        isUpcomingAcademicYear
        isPreviousAcademicYear
      }
    }
  `,
  academicYearGroupBasicDetails: gql`
    fragment academicYearGroupBasicDetailsItem on Organization {
      id
      academicYearGroups {
        id
        academicYears {
          id
        }
      }
    }
  `,
};

export const attachmentGroupsFragment = {
  attachmentGroup: gql`
    fragment attachmentGroupItem on AttachmentGroup {
      id
      metadata
      attachments {
        ...attachmentItem
      }
      studentTemplate {
        id
      }
    }
    ${attachmentFragment.attachment}
  `,
};

export const attachmentNodeFragment = {
  plannerField: gql`
    fragment plannerFieldNodeItem on PlannerField {
      id
      uid
      value
      attachments {
        ...attachmentItem
      }
    }
    ${attachmentFragment.attachment}
  `,

  exemplar: gql`
    fragment exemplarNodeItem on Exemplar {
      id
      attachments {
        ...attachmentItem
      }
    }
    ${attachmentFragment.attachment}
  `,

  questionFieldAttachment: gql`
    fragment questionFieldAttachment on QuestionAttachment {
      id
      attachments {
        ...attachmentItem
      }
    }
    ${attachmentFragment.attachment}
  `,

  incidentDetail: gql`
    fragment incidentDetail on BehaviourIncident {
      id
      attachments {
        ...attachmentItem
      }
    }
    ${attachmentFragment.attachment}
  `,

  incidentConfidentialDetail: gql`
    fragment incidentConfidentialDetail on BehaviourIncident {
      id
      attachments: confidentialAttachments {
        ...attachmentItem
      }
    }
    ${attachmentFragment.attachment}
  `,
  circularDetailsAttachment: gql`
    fragment circularDetailsAttachment on Circular {
      id
      attachments {
        ...attachmentItem
      }
    }
    ${attachmentFragment.attachment}
  `,
};

export const attachmentGroupsNodeFragment = {
  studentAssignmentSubmissionAttachmentGroups: gql`
    fragment studentAssignmentSubmissionAttachmentGroupsItem on StudentAssignmentSubmission {
      id
      attachmentGroups {
        ...attachmentGroupItem
      }
    }
    ${attachmentGroupsFragment.attachmentGroup}
  `,
  studentTemplateAttachmentGroups: gql`
    fragment studentTemplateAttachmentGroupsItem on ResolvedFieldStudentTemplate {
      id
      attachmentGroups {
        ...attachmentGroupItem
      }
    }
    ${attachmentGroupsFragment.attachmentGroup}
  `,
  ltiTemplateAttachmentGroups: gql`
    fragment ltiTemplateAttachmentGroupsItem on ResolvedFieldLtiTemplate {
      id
      attachmentGroups {
        ...attachmentGroupItem
      }
    }
    ${attachmentGroupsFragment.attachmentGroup}
  `,
  openingMessageResourcesAttachmentGroups: gql`
    fragment openingMessageResourcesAttachmentGroupsItem on ResolvedFieldOpeningMessageResource {
      id
      attachmentGroups {
        ...attachmentGroupItem
      }
    }
    ${attachmentGroupsFragment.attachmentGroup}
  `,
};

export const commentReplyMessageFragment = {
  commentReplyMessage: gql`
    fragment commentReplyMessageItem on MessageRepliesConnection {
      edges {
        node {
          id
          label
          createdAt
          createdBy {
            ...userItem
          }
        }
      }
    }
    ${userFragment.basicUserDetails}
  `,
};

export const messageFragment = {
  message: gql`
    fragment messageItem on Message {
      id
      label
      itemType
      itemTypeV2
      metadata
      type
      isCommentRead
      isRead
      isPin
      item {
        id
        ... on AttachmentSet {
          id
          attachments {
            ...attachmentItem
          }
        }
        ... on Assessment {
          id
          title {
            id
            uid
            value
          }
          description {
            id
            uid
            value
          }
          assessmentTool {
            id
            ... on Rubric {
              id
              title
              criterias {
                id
                label
              }
              indicators {
                id
                label
              }
              descriptors {
                id
                label
                criteria {
                  id
                  label
                }
                indicator {
                  id
                  label
                }
              }
              createdBy {
                ...staffItem
              }
            }
            ... on Checklist {
              id
              title
              checklistItems {
                id
                label
              }
              checklistOptionItems {
                id
                label
              }
              createdBy {
                ...staffItem
              }
            }
            ... on SinglePointRubric {
              id
              title
              criterias {
                id
                title
                subTitle
                isCriteriaStandardColumn
              }
              standards {
                id
                title
                subTitle
              }
              createdBy {
                ...staffItem
              }
            }
          }
          fields(
            uids: [
              "goals"
              "voiceInstruction"
              "studentTemplate"
              "measureAssessing"
            ]
          ) {
            id
            uid
            value
            resolvedMinimalTree {
              id
              ... on ResolvedFieldVoiceInstruction {
                id
                attachment {
                  ...attachmentItem
                }
              }
              ... on ResolvedFieldStudentTemplate {
                id
                attachmentGroups {
                  ...attachmentGroupItem
                }
              }
            }
          }
          resources {
            totalCount
            edges {
              node {
                id
                attachments {
                  ...attachmentItem
                }
              }
            }
          }
        }
        ... on AssignmentResource {
          id
          assignmentTitle: title
          label
          voiceInstruction {
            ...attachmentItem
          }
          attachments {
            ...attachmentItem
          }
        }
        ... on StudentAssignmentSubmission {
          id
        }
      }
      createdAt
      createdBy {
        ...userItem
      }
      messageReplies {
        ...commentReplyMessageItem
      }
      isDeleted
    }
    ${attachmentFragment.attachment}
    ${userFragment.basicUserDetails}
    ${attachmentGroupsFragment.attachmentGroup}
    ${commentReplyMessageFragment.commentReplyMessage}
    ${staffFragment.basicStaffDetails}
  `,
  basicMessage: gql`
    fragment basicMessageItem on Message {
      id
      label
      itemType
      createdBy {
        ...userItem
      }
      createdAt
      type
    }
    ${userFragment.basicUserDetails}
  `,
};

export const commentConnectionFragment = {
  commentConnection: gql`
    fragment commentConnectionItem on CommentConnection {
      totalCount
      unreadCount
      unreadCountV2
      pageInfo(first: 10, after: $after) {
        endCursor
        hasNextPage
      }
      edges: messageEdges(first: 10, after: $after) {
        node {
          ...messageItem
        }
      }
    }
    ${messageFragment.message}
  `,
  totalCommentConnection: gql`
    fragment commentConnectionItem on CommentConnection {
      totalCount
      unreadCount
    }
  `,
  basicCommentConnection: gql`
    fragment basicCommentConnectionItem on CommentConnection {
      pageInfo(first: $first, after: $after) {
        hasNextPage
        endCursor
      }
      messageEdges(first: $first, after: $after) {
        node {
          ...basicMessageItem
        }
      }
    }
    ${messageFragment.basicMessage}
  `,
};

export const commentNodeFragment = {
  plannerField: gql`
    fragment plannerFieldNodeItem on PlannerField {
      id
      comments {
        ...commentConnectionItem
      }
    }
    ${commentConnectionFragment.commentConnection}
  `,
  assessment: gql`
    fragment assessmentNodeItem on Assessment {
      id
      comments {
        ...commentConnectionItem
      }
    }
    ${commentConnectionFragment.commentConnection}
  `,
  le: gql`
    fragment leNodeItem on LearningEngagement {
      id
      comments {
        ...commentConnectionItem
      }
    }
    ${commentConnectionFragment.commentConnection}
  `,
  post: gql`
    fragment postItem on Post {
      id
      isDeleted
      comments {
        ...commentConnectionItem
      }
    }
    ${commentConnectionFragment.commentConnection}
  `,
  basicCommentPost: gql`
    fragment basicCommentPostItem on Post {
      id
      comments {
        ...basicCommentConnectionItem
      }
    }
    ${commentConnectionFragment.basicCommentConnection}
  `,
  studentGoalTask: gql`
    fragment studentGoalTaskItem on GoalTask {
      id
      comments {
        ...commentConnectionItem
      }
    }
    ${commentConnectionFragment.commentConnection}
  `,
  communityUnitPlan: gql`
    fragment communityUnitPlanItem on UnitPlan {
      id
      title {
        id
        value
      }
      communityComments {
        ...commentConnectionItem
      }
    }
    ${commentConnectionFragment.commentConnection}
  `,
  communityAssessment: gql`
    fragment communityAssessmentItem on Assessment {
      id
      title {
        id
        value
      }
      communityComments {
        ...commentConnectionItem
      }
    }
    ${commentConnectionFragment.commentConnection}
  `,
  EntityPlannerElementNodeMap: gql`
    fragment EntityPlannerElementNodeMap on EntityPlannerElementNodeMap {
      id
      comments {
        ...commentConnectionItem
      }
    }
    ${commentConnectionFragment.commentConnection}
  `,
  projectComments: gql`
    fragment projectCommentItem on Project {
      id
      projectComments(filters: $commentFilters) {
        ...commentConnectionItem
      }
    }
    ${commentConnectionFragment.commentConnection}
  `,
};

export const totalCommentNodeFragment = {
  plannerField: gql`
    fragment plannerFieldNodeItem on PlannerField {
      id
      comments {
        ...commentConnectionItem
      }
    }
    ${commentConnectionFragment.totalCommentConnection}
  `,
  assessment: gql`
    fragment assessmentNodeItem on Assessment {
      id
      comments {
        ...commentConnectionItem
      }
    }
    ${commentConnectionFragment.totalCommentConnection}
  `,
  le: gql`
    fragment leNodeItem on LearningEngagement {
      id
      comments {
        ...commentConnectionItem
      }
    }
    ${commentConnectionFragment.totalCommentConnection}
  `,
  post: gql`
    fragment postItem on Post {
      id
      comments {
        ...commentConnectionItem
      }
    }
    ${commentConnectionFragment.totalCommentConnection}
  `,
  studentGoalTask: gql`
    fragment studentGoalTaskItem on GoalTask {
      id
      comments {
        ...commentConnectionItem
      }
    }
    ${commentConnectionFragment.totalCommentConnection}
  `,
  communityUnitPlan: gql`
    fragment communityUnitPlanItem on UnitPlan {
      id
      communityComments {
        ...commentConnectionItem
      }
    }
    ${commentConnectionFragment.totalCommentConnection}
  `,
  communityAssessment: gql`
    fragment communityAssessmentItem on Assessment {
      id
      communityComments {
        ...commentConnectionItem
      }
    }
    ${commentConnectionFragment.totalCommentConnection}
  `,
};

export const expertProfileFragment = {
  expert: gql`
    fragment expertItem on ExpertProfile {
      id
      firstName
      lastName
      organization
      profileImage
      description
    }
  `,
};

export const resourceLibraryFragment = {
  resourceFeed: gql`
    fragment resourceLibraryItem on FlowSectionItem {
      id
      title {
        id
        value
      }
      image {
        id
        value
      }
      duration {
        id
        value
      }
      createdBy {
        ...staffItem
      }
      templateId
      ... on Assessment {
        assessmentType {
          id
          value
        }
        taskType {
          id
          type
          label
          color
          displaySequence
          metaData
        }
        assessmentTools {
          edges {
            node {
              id
              assessmentToolType
            }
          }
        }
        grades {
          id
          name
        }

        fields(
          uids: ["duration", "size", "measureAssessing", "assessmentType"]
        ) {
          id
          uid
          value
        }
        isImported
        taggedPractices {
          ...taggedPracticesOnEvidence
        }
      }

      ... on LearningEngagement {
        fields(uids: ["duration", "size", "documentation"]) {
          id
          uid
          value
        }
      }
    }
    ${taggedPracticesFragment.taggedPractices}
    ${staffFragment.basicStaffDetails}
  `,
};

export const fieldScoreFragment = {
  fieldScore: gql`
    fragment fieldScoreItem on ResolvedFieldScore {
      id
      type
      isUsingScore
      maxScore
      category {
        id
        label
        colorCode
        set {
          id
          associatedEntities {
            id
            ... on Course {
              id
              title
            }
            ... on Subject {
              id
              name
            }
          }
        }
      }
    }
  `,
};

export const commonResolvedFieldValueFragment = gql`
  fragment commonResolvedFieldValueItem on ResolvedFieldValue {
    id
    type
    ... on ResolvedFieldBenchmarkSet {
      id
      benchmarks {
        ...benchmarkItem
      }
    }
    ... on ResolvedFieldConceptSet {
      id
      concepts {
        ...conceptItem
      }
    }
    ... on ResolvedFieldActionSet {
      id
      actions {
        ...actionItem
      }
    }
    ... on ResolvedFieldLPSet {
      id
      learnerProfiles {
        ...lpItem
      }
    }
    ... on ResolvedFieldThemeSet {
      id
      themes {
        ...themeItem
      }
    }
    ... on ResolvedFieldSubjectSet {
      id
      subjects {
        ...subjectItem
        subjectGroup {
          id
          name
          constants
          globalSubjectGroup {
            id
            name
            constants
          }
        }
      }
    }
    ... on ResolvedFieldATLSet {
      id
      atls {
        ...atlItem
      }
    }
    ... on ResolvedFieldRelatedConceptSet {
      id
      relatedConcepts {
        ...relatedConceptItem
      }
    }
    ... on ResolvedFieldVoiceInstruction {
      id
      attachment {
        ...attachmentItem
      }
    }
    ... on ResolvedFieldVideoAttachment {
      id
      attachment {
        ...attachmentItem
      }
    }
    ... on ResolvedFieldStudentTemplate {
      id
      attachmentGroups {
        ...attachmentGroupItem
      }
    }
    ... on ResolvedFieldLtiTemplate {
      id
      attachmentGroups {
        ...attachmentGroupItem
      }
    }
    ... on ResolvedFieldTags {
      id
      tags {
        id
        label
      }
    }
    ... on ResolvedFieldGenericTags {
      id
      genericTags {
        id
        label
        __typename
      }
      __typename
    }
    ... on ResolvedFieldPlannerElementSet {
      id
      type
      nodes {
        ...plannerElementNodeItem
      }
    }
    ... on ResolvedFieldSubjectGroup {
      id
      type
      subjectGroups {
        ...subjectGroupWithGlobalSubjectGroupItem
      }
    }
    ... on ResolvedFieldScore {
      ...fieldScoreItem
    }
    ... on ResolvedFieldObjectiveLevel {
      id
      nodes {
        id
        label
        associatedParents {
          id
        }
      }
    }
    ... on ResolvedFieldAttachments {
      id
      type
      attachments {
        ...attachmentItem
      }
    }
  }
`;

export const unitPlanFragment = {
  unitPlanBasicDetails: gql`
    fragment unitPlanBasicDetailsItem on UnitPlan {
      id
      templateId
      grades
      title {
        id
        value
      }
      image {
        id
        value
        resolvedMinimalTree {
          ... on ResolvedFieldVideoAttachment {
            id
            type
            attachment {
              id
              type
              url
              thumbUrl
            }
          }
        }
      }
      allFields {
        id
        uid
        value
        resolvedMinimalTree {
          ...commonResolvedFieldValueItem
        }
      }
      subjects {
        id
        value
      }
      resolvedGrades {
        id
        name
      }
      courses {
        id
      }
      organization {
        id
      }
    }
    ${subjectGroupFragment.subjectGroupBasicDetails}
    ${subjectGroupFragment.subjectGroupWithGlobalSubjectGroup}
    ${plannerElementNodeFragment.plannerElementNode}
    ${pypElementFragment.benchmark}
    ${pypElementFragment.concept}
    ${pypElementFragment.action}
    ${pypElementFragment.lp}
    ${pypElementFragment.theme}
    ${pypElementFragment.atl}
    ${pypElementFragment.relatedConcept}
    ${subjectFragment.subjectBenchmarkDetails}
    ${attachmentFragment.attachment}
    ${attachmentGroupsFragment.attachmentGroup}
    ${fieldScoreFragment.fieldScore}
    ${commonResolvedFieldValueFragment}
  `,
};

export const resolvedFieldValueFragment = {
  resolvedMinimalTree: gql`
    fragment resolvedMinimalTreeItem on ResolvedFieldValue {
      ... on ResolvedFieldUnitPlan {
        id
        type
        unitPlan {
          ...unitPlanBasicDetailsItem
        }
      }
      ...commonResolvedFieldValueItem
    }

    ${subjectGroupFragment.subjectGroupBasicDetails}
    ${plannerElementNodeFragment.plannerElementNode}
    ${pypElementFragment.benchmark}
    ${pypElementFragment.concept}
    ${pypElementFragment.action}
    ${pypElementFragment.lp}
    ${pypElementFragment.theme}
    ${pypElementFragment.atl}
    ${pypElementFragment.relatedConcept}
    ${subjectFragment.subjectBenchmarkDetails}
    ${attachmentFragment.attachment}
    ${attachmentGroupsFragment.attachmentGroup}
    ${fieldScoreFragment.fieldScore}
    ${unitPlanFragment.unitPlanBasicDetails}
    ${commonResolvedFieldValueFragment}
  `,
  resolvedFieldVideoAttachment: gql`
    fragment resolvedFieldVideoAttachmentItem on ResolvedFieldVideoAttachment {
      id
      type
      attachment {
        id
        type
        url
        thumbUrl
      }
    }
  `,
};

export const teacherFragments = {
  teacherFeed: gql`
    fragment teacherItem on Staff {
      id
      profileImage
      firstName
      lastName
      email
      role
      designation {
        ...designationItem
      }
      status
      courses {
        id
        title
      }
    }
    ${designationFragment.designation}
  `,
  teacherDetails: gql`
    fragment teacherItemDetail on Staff {
      id
      firstName
      lastName
      profileImage
      email
      designation {
        id
      }
    }
  `,
};

export const organizationAttendanceOptionFragment = {
  organizationAttendanceOption: gql`
    fragment organizationAttendanceOptionItem on AttendanceOption {
      id
      label
      value
      abbreviation
      value
      color
      status
      isDefault
      sendSMS
      sendEmail
    }
  `,
};

export const meetingFragment = {
  meetingDetails: gql`
    fragment meetingDetailsItem on Meeting {
      id
      label
      createdBy {
        ...staffItem
      }
      createdAt
      urls {
        type
        ... on OtherMeetingUrls {
          url
        }
        ... on ZoomMeetingUrls {
          joinUrl
          startUrl
        }
        ... on MicrosoftTeamMeetingUrls {
          joinUrl
        }
        ... on GoogleMeetingUrls {
          type
          url
        }
      }
      type
    }
    ${staffFragment.basicStaffDetails}
  `,
};

export const userIdentityDetailFragment = {
  userIdentityDetail: gql`
    fragment userIdentityDetailItem on UserIdentityDetail {
      linkedUser {
        id
        firstName
        lastName
        email
        jwt
        organization {
          id
        }
      }
      portalType
    }
  `,
};

export const genericFolderFragment = {
  genericFolderDetails: gql`
    fragment genericFolderItem on GenericFolder {
      id
      name
      color
      createdBy {
        ...userItem
      }
      typeDetails(filters: $filters) {
        id
        ... on FolderAssignmentTypeDetail {
          id
          type
          totalCount(filters: $assignmentFilters)
          stateWiseCount {
            state
            count
          }
        }
      }
    }
    ${userFragment.basicUserDetails}
  `,
  genericFolderBasicDetails: gql`
    fragment genericFolderItem on GenericFolder {
      id
      name
      color
    }
  `,
};

export const evidenceInterfaceFragment = {
  allTypes: gql`
    fragment allEvidenceTypesItem on EvidenceInterface {
      id
      ... on Attachment {
        ...attachmentItem
      }
      ... on Post {
        id
        title
        attachments {
          ...attachmentItem
        }
        items {
          item {
            id
            ... on StudentAssignment {
              id
              assignment {
                id
                content {
                  id
                  ... on Assessment {
                    id
                    assessmentType {
                      id
                      value
                    }
                  }
                }
              }
            }
          }
          itemType
        }
      }
    }
    ${attachmentFragment.attachment}
  `,
};

export const remarkFragment = {
  remarkMessageFragment: gql`
    fragment remarkMessage on Remark {
      id
      message
      createdAt
      isLocked
      lockingState {
        state
        scheduledAt
      }
    }
  `,
};

export const signatureAttachmentFragment = {
  signature: gql`
    fragment signature on Attachment {
      id
      url
      signedUrl
      type
      name
      createdBy {
        id
      }
    }
  `,
};

export const signatureFragment = {
  studentSignature: gql`
    fragment studentSignature on Student {
      ...userNameFields
      signature {
        ...signature
      }
      otherDetails {
        id
        mypPersonalCode
      }
    }
    ${userNameFragment}
    ${signatureAttachmentFragment.signature}
  `,
  supervisorSignature: gql`
    fragment supervisorSignature on Staff {
      ...userNameFields
      isArchived
      isArchivedV2(filters: { academicYearId: $academicYearId })
      signature {
        ...signature
      }
    }
    ${userNameFragment}
    ${signatureAttachmentFragment.signature}
  `,
};

export const staffUnitPlanFragment = {
  staffCourses: gql`
    fragment staffCoursesItem on Staff {
      ...userNameFields
      profileImage
      email
      roles
      role
      isArchived
      courses {
        id
        title
      }
    }
    ${userNameFragment}
  `,
};

export const studentResponseFragment = {
  studentResponse: gql`
    fragment studentResponseItem on StudentAssignment {
      id
      assignment {
        id
        content {
          id
          ... on Assessment {
            id
            title {
              id
              value
            }
            assessmentType {
              id
              value
            }
          }
        }
      }
      submission {
        id
        attachmentGroups {
          id
          attachments {
            ...attachmentItem
          }
        }
      }
      post {
        id
      }
    }
  `,
};

export const yearGroupFragments = {
  basicYearGroupDetails: gql`
    fragment basicYearGroupDetails on YearGroup {
      id
      name
      curriculumProgram {
        id
        type
      }
      grade {
        id
        name
        displaySequence
        courses {
          totalCount
        }
      }
      students {
        totalCount
      }
      examSession {
        id
        session
        year
      }
      selectedTranscriptTerms {
        id
      }
      isArchived
      archivedAt
    }
  `,
};

export const projectGroupFragments = {
  basicProjectGroupDetails: gql`
    fragment basicProjectGroupDetails on ProjectGroup {
      id
      name
      type
      subType
      curriculumProgram {
        id
        type
      }
    }
  `,
  projectGroupDetailsV2: gql`
    fragment projectGroupDetailsV2 on ProjectGroup {
      id
      name
      type
      subType
      curriculumProgram {
        id
        type
        nodeInterfaceType
        label
        acronym
      }
    }
  `,
  projectGroupInfo: gql`
    fragment projectGroupInfo on ProjectGroup {
      id
      name
      type
      subType
      courses {
        edges {
          node {
            id
            title
          }
        }
      }
      subject {
        id
        name
      }
    }
  `,
  projectGroupCourses: gql`
    fragment projectGroupCourses on ProjectGroupCourseConnection {
      edges {
        node {
          id
          title
          calendar {
            id
          }
        }
      }
    }
  `,
};

export const aiFragment = {
  aiMessage: gql`
    fragment aiMessageItem on AiMessage {
      id
      role
      authorType
      content
      metadata
      createdBy {
        ...staffItem
        ...studentItem
      }
      votes {
        myVote
      }
      usageMetricsId
      parentMessage {
        id
      }
    }
    ${staffFragment.basicStaffDetails}
    ${studentFragment.basicStudentDetails}
  `,
};

export const ibPypElementRatingFragment = {
  studentElementRating: gql`
    fragment ratingItem on IBPYPElementRating {
      id
      value
      source
      ibPYPElementId
      ibPYPElementType
      isDeleted
      creatorType
      evaluationJustification {
        id
        message
      }
      academicCriteriaSet {
        id
        label
        academicCriteriaValues {
          id
        }
        criteriaType
        subType
        isArchived
      }
      academicCriteriaValue {
        id
        label
        abbreviation
        description
        color
      }
    }
  `,
};
